import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Drawer, Collapse, List, Typography, Tag, Empty, Spin, App, Input, Button } from 'antd';
import { ClockCircleOutlined, DollarOutlined, ShoppingCartOutlined, SearchOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  fetchProductImages,
  fetchSharedResult,
  fetchUserResults,
  logRequestFailure,
  type HistoryGroup,
  type ProductImages,
} from '../../shared/api/client';
import { parseShareIdInput } from '../../shared/lib/platformShareLink';
import { HISTORY_DRAWER_Z_INDEX } from '../../shared/lib/stickyToolbar';
import type { VisibleBox } from '../../shared/lib/visibleBox';
import { useAuthStore } from '../../shared/session/authStore';
import ProductThumb from '../../shared/ui/ProductThumb';

// Бэкенд отвергает весь батч фото, если в нём есть не-ASIN, — такие ключи групп не шлём.
const ASIN_RE = /^[A-Z0-9]{10}$/;
const THUMB_SIZE = 40;

interface HistoryProps {
  open: boolean;
  onClose: () => void;
  onLoadResult: (fileName: string) => void;
  /**
   * Загрузка расчёта по share-id (или ссылке) из поля поиска — для чужих
   * расчётов, которых в своей истории нет. Данные уже получены, владелец
   * панели их только применяет.
   */
  onLoadShared: (data: Record<string, unknown>) => void;
  /**
   * Стандэлон-хедер рендерится с порталом (дефолт) — там модуль владеет всей
   * страницей, и шторка правомерно занимает весь вьюпорт. `inline` — для
   * монтирования без карточки товара (`UserHistoryBar`): там модуль живёт в
   * слоте/модалке платформы, и с порталом в `document.body` шторка
   * позиционировалась бы от окна и легла бы поверх интерфейса хоста.
   */
  inline?: boolean;
  /**
   * Видимая часть блока модуля (только для `inline`): по ней встаёт панель.
   * Меряет её владелец кнопки — там же живёт якорь, от которого считается блок.
   */
  visibleBox?: VisibleBox | null;
}

const History: React.FC<HistoryProps> = ({ open, onClose, onLoadResult, onLoadShared, inline = false, visibleBox }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isAdmin } = useAuthStore();
  const [groups, setGroups] = useState<HistoryGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [loadingShared, setLoadingShared] = useState(false);
  const [images, setImages] = useState<ProductImages>({});
  // ASIN, за фото которых уже сходили в это открытие панели: без этого каждая
  // перерисовка до ответа слала бы тот же батч заново, а отказ — зацикливал запросы.
  const requestedImages = useRef(new Set<string>());

  useEffect(() => {
    if (!open) return;
    requestedImages.current.clear();
    setSearch('');
    setLoading(true);
    fetchUserResults()
      .then((res) => setGroups(res.groups))
      .catch(() => message.error(t('idea_calc.history.msg_load_error')))
      .finally(() => setLoading(false));
  }, [open]);

  const query = search.trim().replace(/\.json$/i, '').toLowerCase();

  // В реальной базе тысячи расчётов — рендерим не больше MAX_RENDERED_GROUPS
  // групп за раз, иначе Collapse кладёт в DOM тысячи узлов и панель тормозит.
  const MAX_RENDERED_GROUPS = 100;

  const filteredGroups = useMemo(() => {
    if (!query) return groups;
    return groups
      .map((group) => {
        if (group.asin.toLowerCase().includes(query)) return group;
        const versions = group.versions.filter((v) => v.fileName.toLowerCase().includes(query));
        if (versions.length === 0) return null;
        return { ...group, versions, versions_count: versions.length };
      })
      .filter((g): g is HistoryGroup => g !== null);
  }, [groups, query]);

  const visibleGroups = useMemo(() => filteredGroups.slice(0, MAX_RENDERED_GROUPS), [filteredGroups]);
  const hiddenCount = filteredGroups.length - visibleGroups.length;

  // Фото — только для отрисованных групп: промах кэша на бэкенде стоит токен
  // Keepa за ASIN, а у админа в истории тысячи товаров.
  useEffect(() => {
    if (!open) return;
    const missing = visibleGroups
      .map((group) => group.asin)
      .filter((asin) => ASIN_RE.test(asin) && !(asin in images) && !requestedImages.current.has(asin));
    if (missing.length === 0) return;
    missing.forEach((asin) => requestedImages.current.add(asin));
    fetchProductImages(missing)
      .then((loaded) => setImages((prev) => ({ ...prev, ...loaded })))
      // Фото — не данные расчёта: при отказе строка остаётся с рамкой-заглушкой,
      // причина уходит в консоль, повтор — при следующем открытии панели.
      .catch((error: unknown) => logRequestFailure('Product images load error:', error));
  }, [open, visibleGroups, images]);

  const handleLoad = (fileName: string) => {
    onLoadResult(fileName);
    onClose();
  };

  const shareIdInput = parseShareIdInput(search);

  const handleLoadShared = async () => {
    if (!shareIdInput) return;
    setLoadingShared(true);
    try {
      const data = await fetchSharedResult(shareIdInput);
      onLoadShared(data);
      onClose();
    } catch (error: unknown) {
      const notFound = error instanceof ApiError && error.status === 404;
      message.error(t(notFound ? 'idea_calc.history.shared_not_found' : 'idea_calc.history.shared_load_error'));
    } finally {
      setLoadingShared(false);
    }
  };

  const formatDate = (ts: string) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  return (
    <Drawer
      title={
        <span>
          {t('idea_calc.history.title')}
          {isAdmin && <Tag color="red" style={{ marginLeft: 8 }}>Admin</Tag>}
        </span>
      }
      placement="right"
      styles={{ wrapper: { width: 480 } }}
      open={open}
      onClose={onClose}
      {...(inline
        ? {
            // Без маски и портала: хост держит модуль в своей модалке, и шторка
            // не вправе перехватывать клики по всей странице платформы или
            // рисоваться от окна. Абсолютное позиционирование считается от
            // ближайшего relative-предка — в UserHistoryBar/DashboardView он есть.
            mask: false,
            getContainer: false,
            rootStyle: {
              position: 'absolute',
              // Без этого шапка панели прибита к верху всего расчёта и уходит за
              // экран, стоит расчёт прокрутить.
              top: visibleBox?.top ?? 0,
              height: visibleBox?.height,
            },
            // Слой задаётся явно: дефолт приходит из темы ХОСТА (antd —
            // синглтон), и с ним шапка сайдбара уходила под липкую строку.
            zIndex: HISTORY_DRAWER_Z_INDEX,
          }
        : {})}
    >
      <div style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          {t('idea_calc.history.search_label')}
        </Typography.Text>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('idea_calc.history.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Введённое — не имя из своей истории, а share-id или ссылка на чужой расчёт:
            кнопка активна, только когда ввод разбирается как id. */}
        <Button
          size="small"
          icon={<LinkOutlined />}
          style={{ marginTop: 8 }}
          disabled={!shareIdInput}
          loading={loadingShared}
          onClick={handleLoadShared}
        >
          {t('idea_calc.history.load_by_share_button')}
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : groups.length === 0 ? (
        <Empty description={t('idea_calc.history.empty_state')} />
      ) : filteredGroups.length === 0 ? (
        <Empty description={t('idea_calc.history.no_matches')} />
      ) : (
        <>
        <Collapse
          accordion={!query}
          {...(query ? { activeKey: visibleGroups.map((g) => g.asin) } : {})}
          items={visibleGroups.map((group) => ({
            key: group.asin,
            // Стрелка раскрытия — по центру миниатюры, а не у верхнего края строки.
            styles: { header: { alignItems: 'center' } },
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <ProductThumb src={images[group.asin]} size={THUMB_SIZE} alt={group.asin} />
                <span>
                  <Typography.Text strong>{group.asin}</Typography.Text>
                  <Tag style={{ marginLeft: 8 }}>{t('idea_calc.history.versions_count', { count: group.versions_count })}</Tag>
                </span>
              </span>
            ),
            children: (
              <List
                size="small"
                dataSource={group.versions}
                renderItem={(v) => (
                  <List.Item
                    actions={[
                      <Button
                        key="load"
                        size="small"
                        type="primary"
                        ghost
                        icon={<DownloadOutlined />}
                        onClick={() => handleLoad(v.fileName)}
                      >
                        {t('idea_calc.history.load_button')}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          {v.fileName}
                          {v.source === 'mcp' && (
                            <Tag color="purple" title={t('idea_calc.common.source_mcp_tooltip')}>
                              {t('idea_calc.common.source_mcp')}
                            </Tag>
                          )}
                        </span>
                      }
                      description={
                        <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span><ClockCircleOutlined /> {formatDate(v.timestamp)}</span>
                          {v.selling_price != null && (
                            <span><DollarOutlined /> ${v.selling_price}</span>
                          )}
                          {v.monthly_sales != null && (
                            <span><ShoppingCartOutlined /> {t('idea_calc.history.monthly_sales_short', { count: v.monthly_sales })}</span>
                          )}
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            ),
          }))}
        />
        {hiddenCount > 0 && (
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 12 }}>
            {t('idea_calc.history.truncated_hint', { shown: visibleGroups.length, total: filteredGroups.length })}
          </Typography.Text>
        )}
        </>
      )}
    </Drawer>
  );
};

export default History;
